import { Link, useRouter } from '@tanstack/react-router';
import type { ErrorComponentProps } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import { Trans } from '@lingui/react/macro';
import { Loader2, RotateCw, TriangleAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { isExpectedError } from '@/lib/api.ts';

/**
 * Router-level error fallback wired as the router `defaultErrorComponent`. It is
 * applied to every route match, so a content-page throw renders here inside the
 * `_logged` chrome (sidebar + header stay), while a layout/root throw renders it
 * bare — the centered layout reads correctly either way. The homepage link is the
 * escape hatch when a retry keeps failing. Reports the caught error to Sentry:
 * the router swallows route loader/render errors into this boundary, so they never
 * reach Sentry's global handlers — we capture them here instead.
 */
export function RouteError({ error }: ErrorComponentProps) {
  const router = useRouter();
  const [isRetrying, setIsRetrying] = useState(false);

  useEffect(() => {
    // Skip Sentry for expected/operational errors (auth, network, 5xx) — see
    // isExpectedError. A transient failure still renders the retry UI below.
    if (!isExpectedError(error)) Sentry.captureException(error);
  }, [error]);

  // We retry via router.invalidate(), NOT the boundary's `reset`: `reset` only
  // clears the caught error and re-renders, so a *loader* failure immediately
  // re-throws. invalidate() flips the errored match back to pending and re-runs
  // its loader (which refetches, since the failed query holds no cached data),
  // and returns a promise that resolves on either outcome — so `isRetrying` (the
  // spinner) always clears: success unmounts this, a repeat failure remounts it.
  async function handleRetry() {
    setIsRetrying(true);
    try {
      await router.invalidate();
    } finally {
      setIsRetrying(false);
    }
  }

  return (
    <div className='flex min-h-[60vh] flex-col items-center justify-center gap-4 px-6 text-center'>
      <div className='bg-destructive/10 text-destructive flex size-12 items-center justify-center rounded-full'>
        <TriangleAlert className='size-6' />
      </div>
      <div className='space-y-1'>
        <h2 className='text-lg font-semibold tracking-tight'>
          <Trans>Something went wrong</Trans>
        </h2>
        <p className='text-muted-foreground max-w-sm text-sm'>
          <Trans>This page could not be loaded. Please try again.</Trans>
        </p>
      </div>
      <div className='flex flex-wrap items-center justify-center gap-2'>
        <Button className='cursor-pointer' onClick={() => void handleRetry()} disabled={isRetrying}>
          {isRetrying ?
            <Loader2 className='animate-spin' />
          : <RotateCw />}
          {isRetrying ?
            <Trans>Retrying…</Trans>
          : <Trans>Try again</Trans>}
        </Button>
        <Button variant='outline' nativeButton={false} render={<Link to='/' />}>
          <Trans>Go to homepage</Trans>
        </Button>
      </div>
    </div>
  );
}
